/**
 * @fileoverview System-web adapter for the shared active-fire perimeter service.
 * @module system-web/services/activeFirePerimeterService
 *
 * Responsibilities:
 * - Inject the system API endpoint, authenticated HTTP client, and telemetry client.
 * - Preserve the established active-fire service export.
 * Dependencies:
 * - apiFetch, system-web telemetry, and @firesquirrel/fire-hazard-map-layers.
 * Architecture:
 * - Minimal host wiring around package-owned request, telemetry, and error behavior.
 * Notes:
 * - Read-only remote reference data; failures leave existing map source data untouched.
 *
 * @localFirst
 * Source of Truth: system API active-fire reference-data endpoint.
 * Write Flow: None.
 * Sync Strategy: Periodic on-demand reads scheduled by the map caller.
 * Conflict Resolution: Not applicable.
 */

import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';
import {
  createActiveFirePerimeterService,
  createFireHazardTelemetryAdapter,
  createUrlQueryHttpAdapter,
} from '@firesquirrel/fire-hazard-map-layers';

export type { ActiveFirePerimeterFeatureCollection } from '@firesquirrel/fire-hazard-map-layers';

const service = createActiveFirePerimeterService({
  endpoint: '/api/system/fire-hazard/active-fires',
  failureContext: 'fire_active_fires_failed',
  http: createUrlQueryHttpAdapter(async <T>(url: string) => apiFetch(url) as Promise<T>),
  telemetry: createFireHazardTelemetryAdapter(telemetry),
});

/**
 * @process fetchActiveFirePerimeters
 * Steps: Delegate the current perimeter read to the configured shared service.
 * Inputs: None.
 * Outputs: Active-fire GeoJSON FeatureCollection.
 * Failure Handling: Shared service tracks and rethrows failures.
 * Idempotency: REQUIRED for equivalent API state.
 */
export const fetchActiveFirePerimeters = service.fetchActiveFirePerimeters;
